import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, 
  Mic, 
  MessageSquare, 
  Clock, 
  TrendingUp,
  Zap,
  Check,
  ArrowRight,
  Globe,
  Route,
  Users,
  Loader2,
  ArrowLeft,
  Phone,
  Bot,
  Calendar
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackPageView, trackFormSubmit } from '../hooks/useAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const features = [
  {
    icon: Mic,
    title: 'Voice-First Booking',
    description: 'Passengers call and speak naturally. OVA understands "Pick me up at the dialysis center Tuesday at 2pm" without menu trees.',
    highlight: true
  },
  {
    icon: MessageSquare,
    title: 'SMS & Chat',
    description: 'Text-based booking for passengers who prefer typing. Same AI, different channel.'
  },
  {
    icon: Globe,
    title: 'Multi-Language',
    description: 'Spanish, Mandarin, Vietnamese, and more. OVA speaks your passengers\' language.'
  },
  {
    icon: Route,
    title: 'Smart Routing',
    description: 'AI optimizes routes in real-time based on traffic, driver location, and trip priorities.'
  },
  {
    icon: Calendar,
    title: 'Standing Orders',
    description: 'Recurring trips handled automatically. "Every Tuesday and Thursday at 9am" - done.'
  },
  {
    icon: Bot,
    title: 'Driver Dispatch',
    description: 'Drivers receive trips via app with optimized routes, patient info, and turn-by-turn navigation.'
  }
];

const comparisonItems = [
  { feature: 'Natural language booking', old: false, new: true },
  { feature: 'Voice & SMS intake', old: false, new: true },
  { feature: 'Multi-language support', old: false, new: true },
  { feature: 'Real-time route optimization', old: false, new: true },
  { feature: '24/7 automated booking', old: false, new: true },
  { feature: 'No hold times', old: false, new: true },
  { feature: 'Instant trip confirmation', old: false, new: true },
];

export const DispatchPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    fleet_size: '',
    monthly_trips: '',
    current_dispatch: '',
    interested_features: [],
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const dispatchOptions = [
    'Manual (Phone/Paper)', 'TripMaster', 'RouteGenie', 'NEMT Cloud', 'CTS Software', 'Other', 'None'
  ];

  const featureInterests = [
    'Voice Booking', 'SMS Booking', 'Multi-Language', 'Route Optimization', 'Driver App', 'All Features'
  ];

  useEffect(() => {
    trackPageView('/dispatch', 'ova_dispatch');
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMultiSelect = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/products/signup`, {
        ...formData,
        product: 'ova_dispatch',
        tags: ['dispatch', 'ova', 'ai', ...formData.interested_features.map(f => f.toLowerCase().replace(/ /g, '_'))],
        source: 'dispatch_landing_page'
      });
      
      setIsSubmitted(true);
      toast.success('Thank you! Our team will reach out to schedule a demo.');
    } catch (error) {
      console.error('Signup error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-medex-bg flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-8 max-w-lg w-full text-center"
        >
          <div className="w-20 h-20 rounded-full bg-neon-green/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-neon-green" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-white mb-4">
            Demo Request Received!
          </h2>
          <p className="text-slate-400 mb-6">
            Our team will reach out within 24 hours to schedule a personalized demo of OVA Dispatch.
          </p>
          <button onClick={() => navigate('/')} className="btn-secondary">
            Return to Home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-medex-bg">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-neon-cyan/10 via-transparent to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </button>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-neon-cyan/10 border border-neon-cyan/30 mb-6">
                <Brain className="w-4 h-4 text-neon-cyan" />
                <span className="text-sm text-neon-cyan font-medium">OVA Dispatch Engine</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
                AI Dispatch That <span className="text-neon-cyan">Speaks</span> to Your Passengers
              </h1>
              
              <p className="text-xl text-slate-400 mb-8">
                Replace hold times and confusing menus with conversational AI. 
                Passengers book via voice or text in their language. OVA handles the rest.
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="#signup" className="btn-primary inline-flex items-center space-x-2">
                  <span>Request Demo</span>
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a href="#features" className="btn-secondary">
                  See Features
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              {/* Simulated conversation */}
              <div className="glass-card p-6 border-neon-cyan/30 space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 rounded-full bg-neon-cyan/20 flex items-center justify-center flex-shrink-0">
                    <Mic className="w-4 h-4 text-neon-cyan" />
                  </div>
                  <div className="glass p-3 rounded-lg rounded-tl-none max-w-xs">
                    <p className="text-sm text-white">"I need a ride to my dialysis appointment on Tuesday at 2pm"</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 justify-end">
                  <div className="bg-neon-cyan/10 p-3 rounded-lg rounded-tr-none max-w-xs border border-neon-cyan/30">
                    <p className="text-sm text-neon-cyan">"I found your appointment at Fresenius Dialysis Center on Tuesday at 2:00 PM. I'll schedule pickup at 1:15 PM. Would you like a return trip after your appointment?"</p>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-neon-cyan flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 rounded-full bg-neon-cyan/20 flex items-center justify-center flex-shrink-0">
                    <Mic className="w-4 h-4 text-neon-cyan" />
                  </div>
                  <div className="glass p-3 rounded-lg rounded-tl-none max-w-xs">
                    <p className="text-sm text-white">"Yes, around 6pm"</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 justify-end">
                  <div className="bg-neon-cyan/10 p-3 rounded-lg rounded-tr-none max-w-xs border border-neon-cyan/30">
                    <p className="text-sm text-neon-cyan">"Perfect! Both trips are confirmed. You'll receive a text confirmation shortly. Is there anything else I can help with?"</p>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-neon-cyan flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-medex-surface/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Traditional Dispatch vs. <span className="text-neon-cyan">OVA</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card overflow-hidden"
          >
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-slate-400 font-medium">Feature</th>
                  <th className="text-center p-4 text-red-400 font-medium">Legacy Systems</th>
                  <th className="text-center p-4 text-neon-cyan font-medium">OVA Dispatch</th>
                </tr>
              </thead>
              <tbody>
                {comparisonItems.map((item, index) => (
                  <tr key={index} className="border-b border-white/5 last:border-0">
                    <td className="p-4 text-slate-300">{item.feature}</td>
                    <td className="p-4 text-center">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20">
                        <span className="text-red-400">✕</span>
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-neon-green/20">
                        <Check className="w-4 h-4 text-neon-green" />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Dispatch That Works How Humans Think
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              No more training staff on complex software. OVA understands natural requests and handles the logistics.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`glass-card p-6 ${feature.highlight ? 'border-neon-cyan/50 bg-neon-cyan/5' : ''}`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  feature.highlight ? 'bg-neon-cyan/20' : 'bg-neon-cyan/10'
                }`}>
                  <feature.icon className="w-6 h-6 text-neon-cyan" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Signup Form Section */}
      <section id="signup" className="py-20 bg-medex-surface/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              See OVA in Action
            </h2>
            <p className="text-lg text-slate-400">
              Request a personalized demo and see how OVA can transform your dispatch operations.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="glass-card p-8"
          >
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Company Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Contact Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full input-dark"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Fleet Size
                </label>
                <select
                  name="fleet_size"
                  value={formData.fleet_size}
                  onChange={handleChange}
                  className="w-full input-dark"
                >
                  <option value="">Select size</option>
                  <option value="1-5">1-5 vehicles</option>
                  <option value="6-15">6-15 vehicles</option>
                  <option value="16-30">16-30 vehicles</option>
                  <option value="31-50">31-50 vehicles</option>
                  <option value="50+">50+ vehicles</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Current Dispatch System
                </label>
                <select
                  name="current_dispatch"
                  value={formData.current_dispatch}
                  onChange={handleChange}
                  className="w-full input-dark"
                >
                  <option value="">Select system</option>
                  {dispatchOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-3">
                Features You're Most Interested In
              </label>
              <div className="flex flex-wrap gap-2">
                {featureInterests.map(feature => (
                  <button
                    key={feature}
                    type="button"
                    onClick={() => handleMultiSelect('interested_features', feature)}
                    className={`px-4 py-2 rounded-lg text-sm transition-all ${
                      formData.interested_features.includes(feature)
                        ? 'bg-neon-cyan/20 border-neon-cyan/50 text-neon-cyan border'
                        : 'bg-white/5 border-white/10 text-slate-400 border hover:border-white/20'
                    }`}
                  >
                    {feature}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                What's your biggest dispatch challenge?
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={3}
                className="w-full input-dark resize-none"
                placeholder="Tell us about your current pain points..."
              />
            </div>

            {/* SMS Opt-In Section */}
            <div className="mb-6 p-4 rounded-xl bg-white/5 border border-neon-cyan/20">
              <label className="block text-sm font-semibold text-white mb-3">
                Sign up for SMS:
              </label>
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="sms_opt_in"
                  name="sms_opt_in"
                  checked={formData.sms_opt_in || false}
                  onChange={handleChange}
                  className="mt-1 w-5 h-5 rounded border-white/20 bg-white/5 text-neon-cyan focus:ring-neon-cyan/50 cursor-pointer"
                />
                <label htmlFor="sms_opt_in" className="text-sm text-slate-300 cursor-pointer leading-relaxed">
                  <span className="font-medium text-white">I agree to receive automated SMS text messages from MedexOVA</span> at the phone number provided above regarding dispatch updates, service notifications, and important alerts.
                  <span className="block mt-2 text-xs text-slate-400">
                    Message frequency may vary. Standard message and data rates may apply. Reply <span className="font-semibold text-neon-cyan">STOP</span> to opt out. Reply <span className="font-semibold text-neon-cyan">HELP</span> for assistance. Your mobile information will not be sold or shared with third parties for promotional or marketing purposes.
                  </span>
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <span>Request Demo</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </motion.form>
        </div>
      </section>
    </div>
  );
};

export default DispatchPage;
